import { ChatBubbleLeftIcon } from '@heroicons/react/20/solid';
import * as React from 'react';

import { cn } from '@/lib/utils';
import type { ExternalTaskWithLinkSchema } from '@/services/api/generated/externalTaskService';
import type { TaskUpdate } from '@/services/types/client-intake-types';
import { TaskUpdateVisibility } from '@/services/types/client-intake-types';

import { TaskUpdateEditor } from '../TaskUpdateEditor';
import { TimelineEventRouter } from './TimelineEventRouter';
import { TimelineItemComment } from './TimelineItemComment';
import { TimelineItemCorrelated } from './TimelineItemCorrelated';
import type { NoteSourceFilter, TimelineItem } from './types';
import { useTimeline } from './useTimeline';

interface TimelineProps {
  matterId: string;
  taskId: string;
  linkedExternalTasks: ExternalTaskWithLinkSchema[];
  onCreateUpdate: (update: string, visibility: TaskUpdateVisibility, alertMentioned: boolean) => void;
  onUpdateUpdate: (
    updateId: string,
    update: string,
    visibility: TaskUpdateVisibility,
  ) => void;
  onDeleteUpdate: (updateId: string) => void;
}

/**
 * Timeline component for the Updates tab.
 * Shows task-specific events (status changes, assignments, etc.) and user comments.
 * Supports adding, editing, and deleting comments.
 */
const SOURCE_FILTER_OPTIONS: { value: NoteSourceFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'comments', label: 'Comments' },
  { value: 'activity', label: 'Activity' },
];

export const Timeline: React.FC<TimelineProps> = ({
  matterId,
  taskId,
  linkedExternalTasks,
  onCreateUpdate,
  onUpdateUpdate,
  onDeleteUpdate,
}) => {
  const [editingUpdateId, setEditingUpdateId] = React.useState<string | null>(
    null,
  );
  const [sourceFilter, setSourceFilter] =
    React.useState<NoteSourceFilter>('all');
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const { items, isLoading, error } = useTimeline({
    matterId,
    taskId,
    filter: 'task',
    sourceFilter,
  });

  const handleAddUpdate = async (content: string, alertMentioned: boolean) => {
    if (!content.trim()) return;
    onCreateUpdate(content, TaskUpdateVisibility.PRIVATE, alertMentioned);
    setIsEditorOpen(false);
  };

  const handleEditUpdate = (update: TaskUpdate) => {
    setEditingUpdateId(update.id);
  };

  const handleCancelEdit = () => {
    setEditingUpdateId(null);
  };

  const handleSaveEdit = async (
    updateId: string,
    content: string,
    visibility: TaskUpdateVisibility,
  ) => {
    await onUpdateUpdate(updateId, content, visibility);
    setEditingUpdateId(null);
  };

  if (error) {
    return (
      <div className="text-xs font-medium text-red-600 py-4 px-2">
        Failed to load updates
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      {/* Input Area */}
      {isEditorOpen ? (
        <div className="border-b border-slate-200">
          <TaskUpdateEditor onSave={handleAddUpdate} onCancel={() => setIsEditorOpen(false)} autoFocus />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsEditorOpen(true)}
          className="w-full px-2.5 py-2 text-left text-xs font-semibold text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors border-b border-slate-200"
        >
          Write an update…
        </button>
      )}

      {/* Source Filter Pills */}
      <div className="px-2 py-1.5 flex items-center gap-1.5 border-b border-slate-100 bg-slate-50/50">
        {SOURCE_FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setSourceFilter(option.value)}
            className={cn(
              'px-2 py-0.5 text-[10px] font-semibold rounded-full transition-colors',
              sourceFilter === option.value
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Timeline Stream */}
      <div className="divide-y divide-slate-100 max-h-[calc(95vh-380px)] overflow-y-auto scrollbar">
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <div className="animate-pulse text-slate-400 text-xs font-medium">
              Loading updates…
            </div>
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="py-6 flex flex-col items-center justify-center text-gray-300">
            <div className="size-8 rounded-full bg-gray-50 flex items-center justify-center mb-1">
              <ChatBubbleLeftIcon className="size-4 text-gray-200" />
            </div>
            <p className="text-[10px] font-semibold text-gray-400">
              {sourceFilter === 'all' && 'No updates yet'}
              {sourceFilter === 'comments' && 'No comments yet'}
              {sourceFilter === 'activity' && 'No activity yet'}
            </p>
          </div>
        )}
        {!isLoading &&
          items.length > 0 &&
          items.map((item) => (
            <TimelineItemRenderer
              key={item.id}
              item={item}
              matterId={matterId}
              taskId={taskId}
              linkedExternalTasks={linkedExternalTasks}
              editingUpdateId={editingUpdateId}
              onEditUpdate={handleEditUpdate}
              onDeleteUpdate={onDeleteUpdate}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={handleSaveEdit}
              collapseSystemMessages={sourceFilter === 'all'}
            />
          ))}
      </div>
    </div>
  );
};

interface TimelineItemRendererProps {
  item: TimelineItem;
  matterId: string;
  taskId: string;
  linkedExternalTasks: ExternalTaskWithLinkSchema[];
  editingUpdateId: string | null;
  onEditUpdate: (update: TaskUpdate) => void;
  onDeleteUpdate: (updateId: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (
    updateId: string,
    content: string,
    visibility: TaskUpdateVisibility,
  ) => Promise<void>;
  collapseSystemMessages?: boolean;
}

/** Renders the appropriate component for each timeline item type */
const TimelineItemRenderer: React.FC<TimelineItemRendererProps> = ({
  item,
  matterId,
  taskId,
  linkedExternalTasks,
  editingUpdateId,
  onEditUpdate,
  onDeleteUpdate,
  onCancelEdit,
  onSaveEdit,
  collapseSystemMessages = false,
}) => {
  switch (item.type) {
    case 'update':
      return (
        <TimelineItemComment
          update={item.update}
          taskId={taskId}
          linkedExternalTasks={linkedExternalTasks}
          onEdit={onEditUpdate}
          onDelete={onDeleteUpdate}
          isEditing={editingUpdateId === item.update.id}
          onCancelEdit={onCancelEdit}
          onSaveEdit={(content) =>
            onSaveEdit(
              item.update.id,
              content,
              item.update.visibility as TaskUpdateVisibility,
            )
          }
          collapseSystemMessages={collapseSystemMessages}
        />
      );

    case 'correlated':
      return (
        <TimelineItemCorrelated
          activityItem={item.activityItem}
          update={item.update}
          onEdit={onEditUpdate}
          isEditing={editingUpdateId === item.update.id}
          onCancelEdit={onCancelEdit}
          onSaveEdit={(content) =>
            onSaveEdit(
              item.update.id,
              content,
              item.update.visibility as TaskUpdateVisibility,
            )
          }
        />
      );

    case 'event':
      return (
        <TimelineEventRouter
          activityItem={item.activityItem}
          matterId={matterId}
          defaultCollapsed={collapseSystemMessages}
        />
      );

    default:
      return null;
  }
};
